// ============================================
// pages/Settings/settingModal/DeletePinModal.jsx
//
// Reusable modal to delete Admin PIN or Manager PIN.
// Two verification paths the user can choose:
//   Path A — Enter current PIN
//   Path B — Receive OTP on stored email, enter OTP
//
// Usage:
//   <DeletePinModal role="admin"   token={token} onSuccess={() => {}} onClose={() => {}} />
//   <DeletePinModal role="manager" token={token} onSuccess={() => {}} onClose={() => {}} />
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import { X, ShieldCheck, UserCog, KeyRound, Mail, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { API_URL } from '../../../api/config';

const ROLE_META = {
  admin: {
    label:      'Admin PIN',
    color:      'purple',
    Icon:       ShieldCheck,
    headerBg:   'from-purple-600 to-purple-700',
    btnColor:   'bg-red-600 hover:bg-red-700',
    pinBorder:  'border-purple-300 focus:border-purple-500 focus:ring-purple-100',
    otpBorder:  'border-purple-300 focus:border-purple-500 focus:ring-purple-100',
  },
  manager: {
    label:      'Manager PIN',
    color:      'blue',
    Icon:       UserCog,
    headerBg:   'from-blue-600 to-blue-700',
    btnColor:   'bg-red-600 hover:bg-red-700',
    pinBorder:  'border-blue-300 focus:border-blue-500 focus:ring-blue-100',
    otpBorder:  'border-blue-300 focus:border-blue-500 focus:ring-blue-100',
  },
};

export default function DeletePinModal({ role = 'admin', token, onSuccess, onClose }) {
  const meta = ROLE_META[role] || ROLE_META.admin;
  const { Icon } = meta;

  // 'choose' | 'pin' | 'otp'
  const [path, setPath]         = useState('choose');
  const [loading, setLoading]   = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Path A — current PIN
  const [pinDigits, setPinDigits] = useState(['', '', '', '']);
  const pinRefs = useRef([]);

  // Path B — OTP
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otpDigits, setOtpDigits]     = useState(['', '', '', '', '', '']);
  const [otpEmail, setOtpEmail]       = useState('');   // returned by sendDeleteOTP
  const otpRefs = useRef([]);

  // Countdown for resend
  useEffect(() => {
    let t;
    if (countdown > 0) t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Focus first input when switching path
  useEffect(() => {
    if (path === 'pin') setTimeout(() => pinRefs.current[0]?.focus(), 80);
    if (path === 'otp') setTimeout(() => otpRefs.current[0]?.focus(), 80);
  }, [path]);

  // ── Path A: delete via current PIN ─────────────────────
  const handlePinChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...pinDigits]; next[i] = val.slice(-1); setPinDigits(next);
    if (val && i < 3) pinRefs.current[i + 1]?.focus();
    if (i === 3 && val && next.every(d => d)) submitByPin(next.join(''));
  };
  const handlePinKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !pinDigits[i] && i > 0) pinRefs.current[i - 1]?.focus();
  };

  const submitByPin = async (pin) => {
    setLoading(true);
    try {
      const endpoint = role === 'admin' ? 'delete-admin-pin' : 'delete-manager-pin';
      const res  = await fetch(`${API_URL}/pin-settings/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPin: pin }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error(data.message || 'Incorrect PIN');
        setPinDigits(['', '', '', '']);
        setTimeout(() => pinRefs.current[0]?.focus(), 50);
        return;
      }
      toast.success(data.message || `${meta.label} deleted`);
      onSuccess?.();
      onClose?.();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Path B: send OTP ────────────────────────────────────
  const handleSendOTP = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/pin-settings/send-delete-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error(data.message || 'Failed to send OTP');
        return;
      }
      setMaskedEmail(data.maskedEmail || '');
      setOtpEmail(data.storedEmail || data.maskedEmail || '');
      setCountdown(60);
      setPath('otp');
      toast.success(data.message || 'OTP sent');
    } catch {
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Path B: submit OTP ──────────────────────────────────
  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otpDigits]; next[i] = val.slice(-1); setOtpDigits(next);
    if (val && i < 5) otpRefs.current[i + 1]?.focus();
    if (i === 5 && val && next.every(d => d)) submitByOTP(next.join(''));
  };
  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otpDigits[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const submitByOTP = async (otp) => {
    setLoading(true);
    try {
      const endpoint = role === 'admin' ? 'delete-admin-pin' : 'delete-manager-pin';
      const res  = await fetch(`${API_URL}/pin-settings/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ otp, email: otpEmail }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error(data.message || 'Invalid OTP');
        setOtpDigits(['', '', '', '', '', '']);
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
        return;
      }
      toast.success(data.message || `${meta.label} deleted`);
      onSuccess?.();
      onClose?.();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">

        {/* Header */}
        <div className={`bg-gradient-to-br ${meta.headerBg} p-6 text-white relative`}>
          <button
            onClick={onClose}
            disabled={loading}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-40"
          >
            <X size={20} />
          </button>
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-3">
            <Icon size={24} />
          </div>
          <h2 className="text-xl font-bold mb-0.5">Delete {meta.label}</h2>
          <p className="text-white/70 text-xs font-medium">Verify your identity to delete the PIN</p>
        </div>

        {/* Warning banner */}
        <div className="mx-5 mt-5 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
          <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
          <p className="text-xs text-red-700 font-medium leading-relaxed">
            This will permanently remove the {meta.label}. Users will no longer be able to log in with it.
          </p>
        </div>

        <div className="p-5">

          {/* ── CHOOSE PATH ── */}
          {path === 'choose' && (
            <div className="space-y-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
                Choose verification method
              </p>

              <button
                onClick={() => setPath('pin')}
                className="w-full flex items-center gap-4 p-4 border-2 border-slate-200 hover:border-red-400 hover:bg-red-50 rounded-xl transition-all group"
              >
                <div className="w-10 h-10 bg-slate-100 group-hover:bg-red-100 rounded-lg flex items-center justify-center transition-colors">
                  <KeyRound size={18} className="text-slate-500 group-hover:text-red-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-slate-800">Enter Current PIN</p>
                  <p className="text-xs text-slate-500">Use your existing 4-digit PIN</p>
                </div>
              </button>

              <button
                onClick={handleSendOTP}
                disabled={loading}
                className="w-full flex items-center gap-4 p-4 border-2 border-slate-200 hover:border-red-400 hover:bg-red-50 rounded-xl transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-10 h-10 bg-slate-100 group-hover:bg-red-100 rounded-lg flex items-center justify-center transition-colors">
                  {loading
                    ? <Loader2 size={18} className="animate-spin text-slate-400" />
                    : <Mail size={18} className="text-slate-500 group-hover:text-red-600" />
                  }
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-slate-800">Send OTP to Email</p>
                  <p className="text-xs text-slate-500">OTP sent to the stored {role} email</p>
                </div>
              </button>
            </div>
          )}

          {/* ── PATH A: current PIN ── */}
          {path === 'pin' && (
            <div className="space-y-6">
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
                  Enter your current {meta.label}
                </p>
                <div className="flex gap-3 justify-center">
                  {pinDigits.map((d, i) => (
                    <input
                      key={i}
                      ref={el => pinRefs.current[i] = el}
                      type="password"
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={e => handlePinChange(i, e.target.value)}
                      onKeyDown={e => handlePinKeyDown(i, e)}
                      disabled={loading}
                      className={`w-14 h-16 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${meta.pinBorder} disabled:opacity-50`}
                    />
                  ))}
                </div>
                {loading && (
                  <div className="flex items-center justify-center gap-2 mt-4 text-slate-500">
                    <Loader2 size={16} className="animate-spin" />
                    <span className="text-xs font-medium">Verifying...</span>
                  </div>
                )}
              </div>

              <button
                onClick={() => { setPath('choose'); setPinDigits(['','','','']); }}
                className="w-full text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
              >
                ← Try a different method
              </button>
            </div>
          )}

          {/* ── PATH B: OTP ── */}
          {path === 'otp' && (
            <div className="space-y-5">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center gap-2">
                <CheckCircle2 size={14} className="text-green-500 shrink-0" />
                <p className="text-xs text-slate-600 font-medium">
                  OTP sent to <span className="font-bold text-slate-800">{maskedEmail}</span>
                </p>
              </div>

              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                  Enter 6-digit OTP
                </p>
                <div className="flex gap-2 justify-center">
                  {otpDigits.map((d, i) => (
                    <input
                      key={i}
                      ref={el => otpRefs.current[i] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      disabled={loading}
                      className={`w-11 h-13 text-center text-lg font-bold border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${meta.otpBorder} disabled:opacity-50`}
                      style={{ height: '52px' }}
                    />
                  ))}
                </div>
                {loading && (
                  <div className="flex items-center justify-center gap-2 mt-3 text-slate-500">
                    <Loader2 size={16} className="animate-spin" />
                    <span className="text-xs font-medium">Verifying...</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-xs">
                <button
                  onClick={() => { setPath('choose'); setOtpDigits(['','','','','','']); }}
                  className="font-semibold text-slate-500 hover:text-slate-700 transition-colors"
                >
                  ← Different method
                </button>
                {countdown > 0
                  ? <span className="font-bold text-slate-400">Resend in {countdown}s</span>
                  : <button onClick={handleSendOTP} disabled={loading} className="font-bold text-red-600 hover:text-red-700 transition-colors disabled:opacity-50">Resend OTP</button>
                }
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}